import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Icon } from '@/lib/icons';
import { useApp } from '@/hooks/use-app-context';
import { useGlobalSearch } from './use-global-search';
import { buildRouteRegistry, filterRoutesForRole, type SearchRoute } from './route-registry';
import { searchCopy } from './search-copy';

export function GlobalSearchDialog() {
  const { open, setOpen } = useGlobalSearch();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  const { t, user, isAuthenticated } = useApp();
  const copy = searchCopy[lang === 'en' ? 'en' : 'fr'];
  const role = isAuthenticated ? user?.role ?? 'user' : 'guest';

  const routes = useMemo(
    () => filterRoutesForRole(buildRouteRegistry(lang ?? 'fr', t), role),
    [lang, role, t],
  );

  const grouped = useMemo(() => {
    return routes.reduce<Record<string, SearchRoute[]>>((acc, route) => {
      acc[route.group] = acc[route.group] ?? [];
      acc[route.group].push(route);
      return acc;
    }, {});
  }, [routes]);

  const run = (href: string) => {
    setOpen(false);
    navigate(href);
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder={copy.placeholder} />
      <CommandList className="max-h-[420px] bg-popover px-2 py-2">
        <CommandEmpty className="py-8 text-center text-sm text-muted-foreground">
          {copy.empty}
        </CommandEmpty>
        {Object.entries(grouped).map(([group, items]) => (
          <CommandGroup key={group} heading={group}>
            {items.map((route) => (
              <CommandItem
                key={route.href}
                value={[route.title, route.description, ...route.keywords].join(' ')}
                onSelect={() => run(route.href)}
                className="group mb-1 grid cursor-pointer grid-cols-[auto_1fr_auto] items-center gap-3 rounded-2xl px-3 py-3 text-muted-foreground data-[selected=true]:bg-muted/30 data-[selected=true]:text-foreground"
              >
                <span className="grid h-9 w-9 place-items-center rounded-full border border-border/40 bg-card/50 text-muted-foreground group-data-[selected=true]:border-primary/25 group-data-[selected=true]:text-primary">
                  <Icon name={route.icon} className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold">{route.title}</span>
                  {route.description ? (
                    <span className="mt-0.5 block truncate text-xs text-muted-foreground">{route.description}</span>
                  ) : null}
                </span>
                <Icon name="arrowRight" className="h-4 w-4 text-muted-foreground/50 group-data-[selected=true]:text-primary" />
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
}
